import IndexCard from 'components/common/IndexCard';
import CircleLoader from 'components/common/CircleLoader';
import ErrorDisplay from 'components/common/ErrorDisplay';
import { DisplayEventLogEntry } from 'services/backend/eventLog';
import { useOrganizationEventLog } from 'hooks/backend/eventLog';
import { surveyQueries } from 'hooks/backend/surveys';
import { isAxiosError } from 'axios';
import { Decimal } from 'decimal.js-light';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

const EVENT_LOG_GRID_COLUMNS =
  '180px minmax(200px, auto) 100px 100px minmax(175px, 225px) 100px 100px 100px 100px 100px';

const OrganizationEventLogTable = ({
  organizationId,
}: {
  organizationId: number;
}) => {
  const {
    data: organizationEventLogs = [],
    isLoading,
    isError,
    error,
  } = useOrganizationEventLog({ organizationId });

  if (isLoading) {
    return (
      <div className="mt-6">
        <IndexCard>
          <div className="flex flex-col justify-center items-center py-12">
            <CircleLoader isColored />
            <div className="mt-3 text-sm text-gray-600">
              Loading event log...
            </div>
          </div>
        </IndexCard>
      </div>
    );
  }

  if (isError) {
    let errorMessage = 'Failed to load event logs. Please try again.';

    if (isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 404) {
        errorMessage =
          "Organization not found or you don't have permission to view its event logs.";
      } else if (status === 403) {
        errorMessage =
          "You don't have permission to view this organization's event logs.";
      } else if (status && status >= 500) {
        errorMessage =
          'Server error occurred while loading event log. Please try again later.';
      } else if (!navigator.onLine) {
        errorMessage =
          'No internet connection. Please check your connection and try again.';
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }

    return (
      <div className="mt-6">
        <IndexCard>
          <div className="p-4">
            <ErrorDisplay message={errorMessage}>
              <span className="block mt-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                  onClick={() => window.location.reload()}
                >
                  Refresh page
                </button>
              </span>
            </ErrorDisplay>
          </div>
        </IndexCard>
      </div>
    );
  }

  return (
    <div>
      <div className="mt-6">
        <IndexCard>
          <div>
            <div
              className="grid gap-4 justify-items-center py-2 px-4 border-b border-gray-300 text-gray-600 font-normal bg-white sticky top-0 z-10"
              style={{
                gridTemplateColumns: EVENT_LOG_GRID_COLUMNS,
              }}
            >
              <div className="justify-self-start" role="columnheader">
                Date
              </div>
              <div className="justify-self-start" role="columnheader">
                Survey
              </div>
              <div className="justify-self-start" role="columnheader">
                Survey Type
              </div>
              <div className="justify-self-start" role="columnheader">
                Status
              </div>
              <div className="justify-self-start" role="columnheader">
                Who
              </div>
              <div className="justify-self-start" role="columnheader">
                Respondents
              </div>
              <div className="justify-self-start" role="columnheader">
                IR
              </div>
              <div className="justify-self-start" role="columnheader">
                Questions
              </div>
              <div className="justify-self-start" role="columnheader">
                Payment Amount
              </div>
              <div className="justify-self-start" role="columnheader">
                Payment Type
              </div>
            </div>
            <div className="space-y-2">
              <EventLogRows logs={organizationEventLogs} />
            </div>
          </div>
        </IndexCard>
      </div>
    </div>
  );
};

function NoEventsRow() {
  return (
    <div
      className="grid items-center gap-4 py-8 px-1"
      style={{ gridTemplateColumns: EVENT_LOG_GRID_COLUMNS }}
    >
      <div className="col-span-6 text-center text-gray-500">
        <div className="text-sm">No events found.</div>
        <div className="text-xs mt-1">
          Event log entries will appear here when survey status changes occur.
        </div>
      </div>
    </div>
  );
}

function EventLogRow({ event }: { event: DisplayEventLogEntry }) {
  const { data: survey } = useQuery(
    surveyQueries.survey({ surveyId: event.surveyId }),
  );

  return (
    <div
      className="grid items-center justify-items-center gap-4 py-2 px-4 border-b border-gray-300"
      style={{ gridTemplateColumns: EVENT_LOG_GRID_COLUMNS }}
    >
      <div className="justify-self-start text-sm text-gray-900 truncate">
        {new Date(event.createdAt).toLocaleString()}
      </div>
      <div className="justify-self-start text-sm text-gray-900 truncate">
        <Link
          className="block mb-1 cursor-pointer"
          to={`/surveys/${event.surveyId}/analyze`}
        >
          {survey?.title ?? event.surveyId}
        </Link>
      </div>
      <div className="justify-self-start text-sm text-gray-900 truncate">
        {survey?.surveyType ?? 'N/A'}
      </div>
      <div className="justify-self-start text-sm text-gray-900 truncate">
        {event.statusName}
      </div>
      <div className="justify-self-start text-sm text-gray-900">
        {event.isUserSystem ? (
          'System'
        ) : (
          <div>
            <div>{event.userFullName}</div>
            <div className="text-xs text-gray-600">{event.userEmail}</div>
          </div>
        )}
      </div>
      <div className="justify-self-end text-sm text-gray-900">
        {event.respondentCount}
      </div>
      <div className="justify-self-center text-sm text-gray-900">
        {event.incidenceRate}
      </div>
      <div className="justify-self-end text-sm text-gray-900">
        {event.questionCount}
      </div>
      <div className="justify-self-end text-sm text-gray-900">
        {event.paymentAmount ? (
          <div>${new Decimal(event.paymentAmount).toFixed(2)}</div>
        ) : (
          'N/A'
        )}
      </div>
      <div className="justify-self-end text-sm text-gray-900">
        {event.paymentType ? event.paymentType : 'N/A'}
      </div>
    </div>
  );
}

function EventLogRows({ logs }: { logs: DisplayEventLogEntry[] }) {
  if (!logs || !logs.length) {
    return <NoEventsRow />;
  }

  return (
    <>
      {logs.map((event) => (
        <EventLogRow key={event.id} event={event} />
      ))}
    </>
  );
}

export default OrganizationEventLogTable;
